import {
	S3Client,
	GetObjectTaggingCommand,
	ListObjectsV2Command,
} from "@aws-sdk/client-s3";
import type { Group } from "@ptx-autonomous/libs/api";
import {
	convertUserGroupsToTagGroups,
	deleteS3ObjectCompletely,
	listObjectsForUserGroups,
	uploadFileWithTags,
	uploadMultipartFileWithTags,
	type UploadFileOptions,
} from "@ptx-autonomous/libs/s3";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { fetchAuthSession } from "aws-amplify/auth";
import { computed, toValue, type MaybeRefOrGetter } from "vue";
import { VIDEO_EXTENSIONS } from "@shared/types/constants";
import {
	getBaseBeforeLastHyphen,
	getAllQualityVariantKeys,
} from "@shared/utils/video-quality-keys";
export type VideoUrl = string | { "720p"?: string; "1080p"?: string };
export function useHelpFiles(
	userGroups: MaybeRefOrGetter<Group[] | undefined>,
	region: MaybeRefOrGetter<string>,
	bucketName: MaybeRefOrGetter<string>,
) {
	const groups = computed(() => toValue(userGroups) ?? []);
	const currentRegion = computed(() => toValue(region));
	const currentBucket = computed(() => toValue(bucketName));

	async function fetchHelpFiles() {
		const authSession = await fetchAuthSession();

		if (!authSession.credentials?.accessKeyId) {
			throw new Error("Please, login to download the certificate.");
		}

		const s3Client = new S3Client({
			region: currentRegion.value,
			credentials: {
				accessKeyId: authSession.credentials.accessKeyId,
				secretAccessKey: authSession.credentials.secretAccessKey,
				sessionToken: authSession.credentials.sessionToken,
			},
		});

		const result = await listObjectsForUserGroups(
			s3Client,
			currentBucket.value,
			groups.value as string[],
			true,
		);
		return result;
	}

	const query = useQuery({
		queryKey: ["helpFiles", groups, currentRegion, currentBucket],
		queryFn: fetchHelpFiles,
		staleTime: 5 * 60 * 1000,
		retry: 2,
		enabled: computed(() => groups.value.length > 0),
	});

	return {
		files: query.data,
		isLoading: query.isLoading,
		isPending: query.isPending,
		error: query.error,
		refetch: query.refetch,
		queryStatus: query.status,
		errorMessage: computed(() => query.error.value?.message),
	};
}

export interface UploadFileData {
	/** File to upload */
	file?: File | undefined;
	/** S3 object key (file path/name in bucket) */
	key: string;
	/** Array of group names that should have access */
	allowedGroups: string[];
	/** Optional description for the file */
	description?: string;
	/** Optional additional tags */
	additionalTags?: Record<string, string>;
	/** Optional upload configuration */
	options?: UploadFileOptions;
	/** Optional thumbnail file for video */
	thumbnail?: File;
	// if video, then we will need videoFileName
	videoFileName?: string;
	existingThumbnailName?: string;
	category?: string;
	playlistName?: string;
	playlistPosition?: number;
	// NO MORE TAGS CAN BE ADDED WE ARE OUT OF THE TAG LIMIT, IF YOU HAVE TO USE MORE TAGS, MIGRATE TO DATABASE TABLE TO STORE THOSE INFORMATION
}

export interface UploadFileResult {
	/** Whether upload was successful */
	success: boolean;
	/** The S3 key of the uploaded file */
	key: string;
	/** File that was uploaded */
	file: File | undefined;
}

export function useUploadFileMutation(
	region: MaybeRefOrGetter<string>,
	bucketName: MaybeRefOrGetter<string>,
) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: UploadFileData): Promise<UploadFileResult> => {
			try {
				if (!data.key) {
					throw new Error("No file key provided");
				}
				if (!data.allowedGroups || data.allowedGroups.length === 0) {
					throw new Error("No permission groups specified");
				}

				// Get AWS credentials
				const authSession = await fetchAuthSession();

				if (!authSession.credentials?.accessKeyId) {
					throw new Error(
						"Authentication required. Please login to upload files.",
					);
				}
				// Create S3 client - read region fresh here (not captured at setup
				// time) so an in-session region failover is picked up on the next
				// upload rather than staying pointed at the old region.
				const s3Client = new S3Client({
					region: toValue(region),
					credentials: {
						accessKeyId: authSession.credentials.accessKeyId,
						secretAccessKey:
							authSession.credentials.secretAccessKey,
						sessionToken: authSession.credentials.sessionToken,
					},
				});

				console.log(
					`📤 Uploading file: ${data.file?.name ?? "not provided"} to ${data.key}`,
				);

				// Upload the file
				const success = await uploadFileForGroups(
					s3Client,
					toValue(bucketName),
					data.key,
					data.file,
					data.allowedGroups,
					data.description,
					data.additionalTags,
					data.options,
					data.thumbnail,
					data.existingThumbnailName,
					data.category,
					data.playlistName,
					data.playlistPosition,
				);

				if (!success) {
					throw new Error(
						`Upload operation failed for file: ${data.file?.name}`,
					);
				}

				return {
					success,
					key: data.key,
					file: data.file,
				};
			} catch (error) {
				console.error(`Upload error for ${data.file?.name}:`, error);
				throw error;
			}
		},
		onSuccess: () =>
			queryClient.invalidateQueries({ queryKey: ["helpFiles"] }),
		onError: (error, variables) => {
			console.error(
				`❌ Failed to upload: ${variables.file?.name}`,
				error,
			);
		},
	});
}

export function useDeleteFileMutation(
	region: MaybeRefOrGetter<string>,
	bucketName: MaybeRefOrGetter<string>,
) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (s3Key: string): Promise<boolean> => {
			try {
				if (!s3Key) {
					throw new Error("No file key provided");
				}

				// Get AWS credentials
				const authSession = await fetchAuthSession();

				if (!authSession.credentials?.accessKeyId) {
					throw new Error(
						"Authentication required. Please login to delete files.",
					);
				}

				const currentBucket = toValue(bucketName);

				// Create S3 client - read region fresh here (not captured at
				// setup time) so an in-session region failover is picked up on
				// the next delete rather than staying pointed at the old region.
				const s3Client = new S3Client({
					region: toValue(region),
					credentials: {
						accessKeyId: authSession.credentials.accessKeyId,
						secretAccessKey:
							authSession.credentials.secretAccessKey,
						sessionToken: authSession.credentials.sessionToken,
					},
				});

				const originalKey = s3Key;
				const ext = s3Key.toLowerCase().slice(s3Key.lastIndexOf("."));
				const isVideo = VIDEO_EXTENSIONS.includes(ext);
				const keysToDelete = new Set<string>([s3Key]);

				// if we have a video, we need to find all quality variants and delete them as well
				if (isVideo) {
					const base = getBaseBeforeLastHyphen(s3Key);
					if (base) {
						const prefix = base + "-";
						let continuationToken: string | undefined;
						do {
							const listResp = await s3Client.send(
								// it returns all objects with the prefix, we filter them to find variants
								new ListObjectsV2Command({
									Bucket: currentBucket,
									Prefix: prefix,
									ContinuationToken: continuationToken,
								}),
							);
							for (const obj of listResp.Contents ?? []) {
								if (obj.Key) {
									const objExt = obj.Key.toLowerCase().slice(
										obj.Key.lastIndexOf("."),
									);
									if (VIDEO_EXTENSIONS.includes(objExt)) {
										keysToDelete.add(obj.Key);
									}
								}
							}
							continuationToken = listResp.IsTruncated
								? listResp.NextContinuationToken
								: undefined;
						} while (continuationToken);
					} else {
						getAllQualityVariantKeys(s3Key).forEach((k: string) =>
							keysToDelete.add(k),
						);
					}
				}

				let lastError: Error | null = null;
				let thumbnailKeysToDelete: string | undefined = undefined;
				for (const key of keysToDelete) {
					try {
						const tag = await getObjectTags(
							s3Client,
							currentBucket,
							key,
						);
						const thumbnailKey = tag["thumbnail-name"];
						if (thumbnailKey) thumbnailKeysToDelete = thumbnailKey;
					} catch {
						console.warn(
							`Could not get tags for ${key}, skipping thumbnail deletion`,
						);
						// there is possibility of not having thumbnails so we are not throwing error here, just logging and moving on with deletion of main files.
					}
				}
				if (
					thumbnailKeysToDelete !== undefined &&
					thumbnailKeysToDelete !== null
				) {
					try {
						const ok = await deleteS3ObjectCompletely(
							s3Client,
							currentBucket,
							thumbnailKeysToDelete,
						);
						if (!ok) {
							console.error(
								`failed to delete the thumbnail ${thumbnailKeysToDelete}`,
							);
							lastError = new Error(
								`deletion failed for thumbnail ${thumbnailKeysToDelete}`,
							);
						}
					} catch (err) {
						console.warn(
							`Delete thumbnail error for ${thumbnailKeysToDelete}:`,
							err,
						);
						if (err instanceof Error) lastError = err;
					}
				}
				for (const key of keysToDelete) {
					// now deleting the main files, we do this after attempting thumbnail deletion to avoid orphaned thumbnails in case of error during main file deletion
					try {
						const success = await deleteS3ObjectCompletely(
							s3Client,
							currentBucket,
							key,
						);
						if (!success) {
							// For derived quality-variant keys it's safe to ignore failures
							// (e.g. when 720p/1080p variants don't actually exist).
							// Only surface an error if the original requested key fails.
							if (key === originalKey) {
								lastError = new Error(
									`Delete operation failed for file: ${key}`,
								);
							}
						}
					} catch (err) {
						console.warn(`Delete skip or error for ${key}:`, err);
						if (err instanceof Error && key === originalKey) {
							lastError = err;
						}
					}
				}

				if (lastError) {
					throw lastError;
				}
				return true;
			} catch (error) {
				console.error(`Delete error for ${s3Key}:`, error);
				throw error;
			}
		},
		onSuccess: () =>
			queryClient.invalidateQueries({ queryKey: ["helpFiles"] }),
		onError: (error, s3Key) => {
			console.error(`❌ Failed to delete: ${s3Key}`, error);
		},
	});
}

export const getObjectTags = async (
	S3Client: S3Client,
	bucketName: string,
	key: string,
): Promise<Record<string, string>> => {
	try {
		const command = new GetObjectTaggingCommand({
			Bucket: bucketName,
			Key: key,
		});
		const response = await S3Client.send(command);
		const tags: Record<string, string> = {};
		response.TagSet?.forEach(tag => {
			if (tag.Key && tag.Value) {
				tags[tag.Key] = tag.Value;
			}
		});
		return tags;
	} catch (error) {
		console.error(`Error fetching tags for ${key}:`, error);
		throw error;
	}
};
/**
 * Helper function to create permission tags for user groups
 *
 * @param groups - Array of group names that should have access
 * @param additionalTags - Optional additional tags to include
 *
 * @returns Record<string, string> - Tags object with group permissions and additional tags
 *
 * @example
 * ```typescript
 * const tags = createPermissionTags(
 *   ['TechSupport', 'Engineering'],
 *   { 'description': 'System documentation', 'version': '2.1' }
 * );
 * // Result: { 'TechSupport': 'allow', 'Engineering': 'allow', 'description': 'System documentation', 'version': '2.1' }
 * ```
 */
export const createPermissionTags = (
	userGroups: string[],
	additionalTags: Record<string, string> = {},
): Record<string, string> => {
	const permissionTags: Record<string, string> = {};

	// Convert user groups to tag groups and add permission tags
	const tagGroups = convertUserGroupsToTagGroups(userGroups);
	tagGroups.forEach((tagGroup: string) => {
		permissionTags[tagGroup] = "allow";
	});

	// Merge with additional tags
	return { ...permissionTags, ...additionalTags };
};

/**
 * Upload a file with permission-based tags for specific user groups
 *
 * @param s3Client - AWS S3 client instance
 * @param bucket - S3 bucket name
 * @param key - S3 object key
 * @param file - File to upload
 * @param allowedGroups - Array of group names that should have access
 * @param description - Optional description for the file
 * @param additionalTags - Optional additional tags
 * @param options - Optional upload configuration
 *
 * @returns Promise<boolean> - Success status
 *
 * @example
 * ```typescript
 * const success = await uploadFileForGroups(
 *   s3Client,
 *   'help-files-bucket',
 *   'manuals/setup-guide.pdf',
 *   fileObject,
 *   ['TechSupport', 'Engineering'],
 *   'Complete setup guide for new installations',
 *   { 'category': 'documentation', 'version': '1.0' }
 * );
 * ```
 */
export const uploadFileForGroups = async (
	s3Client: S3Client,
	bucket: string,
	key: string,
	file: File | undefined,
	allowedUserGroups: string[],
	description?: string,
	additionalTags: Record<string, string> = {},
	options: UploadFileOptions = {},
	thumbnail?: File | undefined,
	existingThumbnailName?: string,
	category?: string,
	playlistName?: string,
	playlistPosition?: number,
): Promise<boolean> => {
	const tags = createPermissionTags(allowedUserGroups, additionalTags);
	// NOTE: NO MORE NEW TAGS CAN BE ADDED WE ARE OUT OF THE TAG LIMIT, IF YOU HAVE TO USE MORE TAGS, MIGRATE TO DATABASE TABLE TO STORE THOSE INFORMATION
	const isVideoByKey = VIDEO_EXTENSIONS.some((ext: string) =>
		key.toLowerCase().endsWith(ext),
	);
	const isVideoUpload =
		(file?.type?.startsWith("video/") ?? false) || isVideoByKey;
	if (thumbnail && isVideoUpload) {
		const hasExt = /\.([^/]+)$/.test(key);
		const baseKey = hasExt
			? key.replace(/\.[^.]+$/, "-thumbnail")
			: key + "-thumbnail";
		const thumbExt =
			thumbnail.name.split(".").pop()?.toLowerCase() || "jpg";
		const thumbnailKey = `${baseKey}.${thumbExt}`;

		const thumbnailTags = createPermissionTags(allowedUserGroups, {
			"thumbnail-for": key,
			description: `Thumbnail for ${description || key}`,
		});

		const thumbnailUploadSuccess = await uploadFileWithTags(
			s3Client,
			bucket,
			thumbnailKey,
			thumbnail,
			thumbnailTags,
			options,
		);

		if (!thumbnailUploadSuccess) {
			console.error(`❌ Failed to upload thumbnail: ${thumbnail.name}`);
			return false;
		}

		tags["thumbnail-name"] = thumbnailKey;
	} else if (existingThumbnailName) {
		tags["thumbnail-name"] = existingThumbnailName;
	}

	// Add description if provided
	if (description) {
		tags.description = description;
	}

	console.log(
		`📤 Uploading file with user groups: [${allowedUserGroups.join(", ")}]`,
	);
	console.log(
		`🏷️  S3 tags will be: [${Object.keys(tags)
			.filter(k => k !== "description")
			.join(", ")}]`,
	);
	const fileSizeGB = file ? file.size / (1024 * 1024 * 1024) : 0;
	tags["category"] = category || "others";
	if (playlistName) {
		const playlist = playlistName + "@@" + playlistPosition?.toString();
		tags["playlist"] = playlist;
	}
	if (fileSizeGB > 5) {
		console.log(
			`large file detected (${fileSizeGB.toFixed(2)} GB), using multipart upload`,
		);
		return uploadMultipartFileWithTags(
			s3Client,
			bucket,
			key,
			file!,
			tags,
			options,
		);
	} else {
		return uploadFileWithTags(s3Client, bucket, key, file, tags, options);
	}
};
